import React, { useState, useEffect } from 'react';
import { functions } from '../firebase';
import logo from '../assets/lvmh-logo.png'; // Assuming asset exists, or we use text if not

interface PasswordGateProps {
    children: React.ReactNode;
}

export const PasswordGate: React.FC<PasswordGateProps> = ({ children }) => {
    const [password, setPassword] = useState('');
    const [isAuthorized, setIsAuthorized] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [checkingSession, setCheckingSession] = useState(true);

    useEffect(() => {
        // Check local storage for existing session
        const storedAuth = localStorage.getItem('lvmh_auth_token');
        const storedTime = localStorage.getItem('lvmh_auth_time');

        // Simple expiration check (e.g. 24h)
        const now = Date.now();
        const AC_VALIDITY = 24 * 60 * 60 * 1000;

        if (storedAuth === 'valid' && storedTime && (now - parseInt(storedTime)) < AC_VALIDITY) {
            setIsAuthorized(true);
        }
        setCheckingSession(false);
    }, []);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            const verifyAppPassword = functions.httpsCallable('verifyAppPassword');
            const result = await verifyAppPassword({ password });
            const data = result.data as { success: boolean };

            if (data.success) {
                localStorage.setItem('lvmh_auth_token', 'valid');
                localStorage.setItem('lvmh_auth_time', Date.now().toString());
                setIsAuthorized(true);
            } else {
                setError("Mot de passe incorrect");
            }
        } catch (err) {
            console.error("Auth error", err);
            setError("Erreur de verification. Veuillez réessayer.");
        } finally {
            setLoading(false);
        }
    };

    if (checkingSession) {
        return (
            <div className="min-h-screen bg-neutral-900 flex items-center justify-center text-white">
                <div className="animate-pulse">Chargement...</div>
            </div>
        );
    }

    if (isAuthorized) {
        // Render the app if authorized
        return <>{children}</>;
    }

    return (
        <div className="min-h-screen bg-black flex flex-col items-center justify-center p-4">
            <div className="w-full max-w-md bg-neutral-900 rounded-xl p-8 shadow-2xl border border-neutral-800">
                <div className="text-center mb-8">

                    <p className="text-neutral-500 text-sm uppercase tracking-wider">Video Editor Access</p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                        <input
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            placeholder="Mot de passe"
                            className="w-full bg-neutral-800 border-none rounded-lg py-4 px-4 text-white placeholder-neutral-500 focus:ring-1 focus:ring-white transition-all text-center text-lg tracking-widest"
                            disabled={loading}
                            autoFocus
                        />
                    </div>

                    {error && (
                        <div className="text-red-500 text-center text-sm bg-red-500/10 py-2 rounded">
                            {error}
                        </div>
                    )}

                    <button
                        type="submit"
                        disabled={loading || !password}
                        className={`w-full py-4 rounded-lg font-medium text-black uppercase tracking-wider transition-all
              ${loading || !password
                                ? 'bg-neutral-600 cursor-not-allowed'
                                : 'bg-white hover:bg-neutral-200 shadow-lg shadow-white/10'}`}
                    >
                        {loading ? 'Vérification...' : 'Entrer'}
                    </button>
                </form>

                <div className="mt-8 text-center">
                    <p className="text-xs text-neutral-600">Secure Access • Internal Use Only</p>
                </div>
            </div>
        </div>
    );
};
